<template>
  <div
    ref="mindmapContainer"
    class="mindmap-card"
  >
    <div class="mindmap-content">
      <!-- Titre central -->
      <div class="central-node">
        <div class="central-icon">
          🧠
        </div>
        <div
          class="central-title"
          v-html="renderMath(mindMapData.centralConcept || 'Carte Mentale')"
        />
      </div>

      <!-- Branches principales -->
      <div class="branches-container">
        <div
          v-for="(branch, bIndex) in mindMapData.branches || []"
          :key="bIndex"
          class="branch"
          :class="'branch-' + (bIndex % 4)"
        >
          <div class="branch-header">
            <span class="branch-icon">{{ branch.icon || getDefaultIcon(bIndex) }}</span>
            <span
              class="branch-title"
              v-html="renderMath(branch.title)"
            />
          </div>

          <!-- Sous-branches -->
          <div class="sub-branches">
            <div
              v-for="(sub, sIndex) in branch.items || []"
              :key="sIndex"
              class="sub-branch"
              :class="'sub-' + (bIndex % 4)"
            >
              <span class="sub-bullet" />
              <span
                class="sub-text"
                v-html="renderMath(sub)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bouton export -->
    <button
      v-if="showExport"
      class="export-btn"
      @click="exportToPNG"
    >
      <span>📥</span> Télécharger PNG
    </button>
  </div>
</template>

<script>
import { renderLatex } from '@/libs/katexRender'

export default {
  name: 'MindMapCard',
  props: {
    mindMapData: {
      type: Object,
      required: true,
      default: () => ({
        centralConcept: 'Concept Central',
        branches: [],
      }),
    },
    showExport: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      icons: ['📊', '💡', '⚡', '🎯', '🔧', '📈', '🌟', '🔑'],
    }
  },
  methods: {
    renderMath(text) {
      return renderLatex(text)
    },
    getDefaultIcon(index) {
      return this.icons[index % this.icons.length]
    },
    async exportToPNG() {
      try {
        const html2canvas = (await import('html2canvas')).default
        const container = this.$refs.mindmapContainer

        const canvas = await html2canvas(container, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          useCORS: true,
        })

        const link = document.createElement('a')
        link.download = `carte-mentale-${Date.now()}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
      } catch (error) {
        console.error('Erreur export PNG:', error)
        this.$emit('export-error', error)
      }
    },
  },
}
</script>

<style scoped>
.mindmap-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 32px 28px;
  padding-bottom: 64px;
  position: relative;
  overflow: hidden;
  min-height: 400px;
}

.mindmap-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 10% 90%, rgba(56, 182, 255, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 90% 10%, rgba(255, 196, 0, 0.05) 0%, transparent 50%);
  pointer-events: none;
}

.mindmap-content {
  position: relative;
  z-index: 1;
}

/* Central Node */
.central-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
}

.central-icon {
  font-size: 36px;
  margin-bottom: 10px;
  filter: drop-shadow(0 2px 6px rgba(56, 182, 255, 0.3));
}

.central-title {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  text-align: center;
  padding: 14px 28px;
  background: linear-gradient(135deg, #38B6FF, #38B6FF);
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(56, 182, 255, 0.25);
  letter-spacing: -0.2px;
}

/* Branches Container */
.branches-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

@media (max-width: 600px) {
  .branches-container {
    grid-template-columns: 1fr;
  }
}

/* Branch */
.branch {
  border-radius: 14px;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
}

.branch:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
}

/* Branch color variants */
.branch-0 { border-top: 3px solid #38B6FF; }
.branch-1 { border-top: 3px solid #FFC400; }
.branch-2 { border-top: 3px solid #38B6FF; }
.branch-3 { border-top: 3px solid #38B6FF; }

.branch-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  font-weight: 700;
  font-size: 14px;
  color: #1a1a2e;
  background: #fafbfc;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.branch-0 .branch-header { color: #38B6FF; }
.branch-1 .branch-header { color: #b8860b; }
.branch-2 .branch-header { color: #38B6FF; }
.branch-3 .branch-header { color: #38B6FF; }

.branch-icon {
  font-size: 20px;
}

.branch-title {
  flex: 1;
  font-size: 14px;
}

/* Sub-branches */
.sub-branches {
  padding: 10px 16px 14px;
}

.sub-branch {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 7px 0;
  padding-left: 14px;
  border-left: 2px solid #e8e8e8;
  margin-left: 8px;
}

.sub-0 { border-left-color: rgba(56, 182, 255, 0.3); }
.sub-1 { border-left-color: rgba(255, 196, 0, 0.4); }
.sub-2 { border-left-color: rgba(56, 182, 255, 0.3); }
.sub-3 { border-left-color: rgba(56, 182, 255, 0.3); }

.sub-bullet {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 7px;
  flex-shrink: 0;
  background: #38B6FF;
}

.sub-1 .sub-bullet { background: #FFC400; }
.sub-2 .sub-bullet { background: #38B6FF; }

.sub-text {
  color: #444;
  font-size: 13px;
  line-height: 1.55;
}

/* Export Button */
.export-btn {
  position: absolute;
  bottom: 14px;
  right: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(56, 182, 255, 0.08);
  border: 1px solid rgba(56, 182, 255, 0.2);
  border-radius: 10px;
  color: #38B6FF;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
}

.export-btn:hover {
  background: rgba(56, 182, 255, 0.15);
  transform: translateY(-1px);
}
</style>
